import {
  CALIBRATION_STANDARD_LABEL_BY_ID,
  CALIBRATION_STANDARD_SOLUTION_IDS,
  UNKNOWN_SAMPLE_SOLUTION_ID,
  WAVELENGTHS
} from "@/labs/analytical-instrumentation-lab/experimentPlugin.js";

const REQUIRED_WAVELENGTHS_NM = WAVELENGTHS.map((w) => w.wavelength);

const STANDARD_ID_SET = new Set(CALIBRATION_STANDARD_SOLUTION_IDS);

const MIN_UNKNOWN_REPLICATES_AT_ONE_WAVELENGTH = 3;

function isUnknownMeasurement(m) {
  return (
    m.wavelength != null &&
    (m.isUnknown === true || m.solutionId === UNKNOWN_SAMPLE_SOLUTION_ID)
  );
}

/**
 * Step 1: at least one standard (non-blank, non-unknown) measurement per LED wavelength.
 * @param {Array<{ wavelength?: number, solutionId?: string, isUnknown?: boolean }>} measurements
 * @returns {{ satisfied: boolean, missingWavelengths: number[] }}
 */
export function checkWavelengthSelectionStep(measurements = []) {
  const standardMeasurements = measurements.filter(
    (m) => m.solutionId !== "blank" && !m.isUnknown
  );
  const covered = new Set(
    standardMeasurements.map((m) => m.wavelength).filter((nm) => nm != null)
  );
  const missingWavelengths = REQUIRED_WAVELENGTHS_NM.filter(
    (nm) => !covered.has(nm)
  );
  return {
    satisfied: missingWavelengths.length === 0,
    missingWavelengths
  };
}

/**
 * Step 2: each calibration standard A–I measured at least once at some common wavelength.
 * @param {Array<{ wavelength?: number, solutionId?: string, isUnknown?: boolean }>} measurements
 * @returns {{
 *   satisfied: boolean,
 *   bestWavelength?: number | null,
 *   missingSolutionIds?: string[],
 *   missingLabels?: string[]
 * }}
 */
export function checkStandardSamplesStep(measurements = []) {
  const candidates = measurements.filter(
    (m) =>
      m.wavelength != null &&
      !m.isUnknown &&
      m.solutionId &&
      STANDARD_ID_SET.has(m.solutionId)
  );

  const byWavelength = new Map();
  for (const m of candidates) {
    const wl = m.wavelength;
    if (!byWavelength.has(wl)) {
      byWavelength.set(wl, new Set());
    }
    byWavelength.get(wl).add(m.solutionId);
  }

  const need = CALIBRATION_STANDARD_SOLUTION_IDS.length;

  for (const [wl, ids] of byWavelength) {
    if (ids.size === need) {
      return { satisfied: true, bestWavelength: wl };
    }
  }

  let bestWl = /** @type {number | null} */ (null);
  let bestSet = new Set();
  for (const [wl, ids] of byWavelength) {
    if (ids.size > bestSet.size) {
      bestWl = wl;
      bestSet = ids;
    }
  }

  const missingSolutionIds = CALIBRATION_STANDARD_SOLUTION_IDS.filter(
    (id) => !bestSet.has(id)
  );
  const missingLabels = missingSolutionIds.map(
    (id) => CALIBRATION_STANDARD_LABEL_BY_ID[id] ?? id
  );

  return {
    satisfied: false,
    bestWavelength: bestWl,
    missingSolutionIds,
    missingLabels
  };
}

/**
 * Step 3: at least three unknown-sample measurements at one wavelength.
 * @param {Array<{ wavelength?: number, solutionId?: string, isUnknown?: boolean }>} measurements
 * @returns {{
 *   satisfied: boolean,
 *   bestWavelength?: number | null,
 *   countAtBest?: number
 * }}
 */
export function checkUnknownSampleStep(measurements = []) {
  const unknownRows = measurements.filter(isUnknownMeasurement);

  const byWavelength = new Map();
  for (const m of unknownRows) {
    const wl = m.wavelength;
    byWavelength.set(wl, (byWavelength.get(wl) ?? 0) + 1);
  }

  for (const [wl, count] of byWavelength) {
    if (count >= MIN_UNKNOWN_REPLICATES_AT_ONE_WAVELENGTH) {
      return {
        satisfied: true,
        bestWavelength: wl,
        countAtBest: count
      };
    }
  }

  let bestWl = /** @type {number | null} */ (null);
  let bestCount = 0;
  for (const [wl, count] of byWavelength) {
    if (count > bestCount) {
      bestWl = wl;
      bestCount = count;
    }
  }

  return {
    satisfied: false,
    bestWavelength: bestWl,
    countAtBest: bestCount
  };
}

function unknownDataExitWarning(measurements) {
  const { satisfied } = checkUnknownSampleStep(measurements);
  if (satisfied) return null;

  return {
    title: "Incomplete measurements",
    body: "Step 3 requires three measurements of the unknown sample at the same wavelength (your chosen optimal wavelength). Are you sure you want to proceed?"
  };
}

function dataCollectionExitWarning(measurements) {
  const { satisfied } = checkStandardSamplesStep(measurements);
  if (satisfied) return null;

  return {
    title: "Incomplete measurements",
    body: "Step 2 requires each standard sample (A through I) to be measured at least once at the same wavelength (your chosen optimal wavelength). Are you sure you want to proceed?"
  };
}

function wavelengthSelectionExitWarning(measurements) {
  const { satisfied } = checkWavelengthSelectionStep(measurements);
  if (satisfied) return null;
  return {
    title: "Incomplete measurements",
    body: "Step 1 requires a standard measurement at each wavelength: 430, 470, 565, and 635 nm. Are you sure you want to proceed?"
  };
}

/**
 * Maps lab id → passage id → (measurements) => null | { title, body }.
 * Re-exported through each lab’s `module.js`; app code uses `getExitWarning` / `getStepExitChecks` from `@/labRegistry.js`.
 */
export const STEP_EXIT_CHECKS = {
  "analytical-instrumentation-lab": {
    "wavelength-selection": wavelengthSelectionExitWarning,
    "data-collection": dataCollectionExitWarning,
    "unknown-data": unknownDataExitWarning
  }
};

/**
 * @param {{ labId: string, passageId: string, measurements: unknown[] }} args
 * @returns {{ title: string, body: string } | null}
 */
export function getExitWarning({ labId, passageId, measurements }) {
  const byPassage = STEP_EXIT_CHECKS[labId];
  if (!byPassage) return null;
  const fn = byPassage[passageId];
  if (typeof fn !== "function") return null;
  return fn(measurements ?? []);
}
